// src/hooks/useAnalytics.ts
/**
 * Custom React Hooks for Firebase Analytics
 * Automatically tracks page views, time on page, and user interactions
 */

import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { logPageView, logTiming, logButtonClick } from '../firebase/analytics';

/**
 * Hook to automatically track page views and time on page
 */
export const usePageTracking = (pageName?: string) => {
  const location = useLocation();
  const startTimeRef = useRef<number>(Date.now());
  const currentPathRef = useRef<string>(location.pathname);

  useEffect(() => {
    // Log page view
    const pageTitle = pageName || document.title;
    logPageView(location.pathname, pageTitle);

    // Reset start time for new page
    startTimeRef.current = Date.now();
    currentPathRef.current = location.pathname;

    // Cleanup function to log time on page when leaving
    return () => {
      const timeOnPage = Date.now() - startTimeRef.current;
      logTiming('time_on_page', timeOnPage, currentPathRef.current);
    };
  }, [location.pathname, pageName]);
};

/**
 * Hook to track button clicks with analytics
 * Returns a wrapped click handler that logs before executing
 */
export const useTrackedClick = (
  buttonName: string,
  onClick?: () => void
) => {
  const location = useLocation();

  return useCallback(() => {
    logButtonClick(buttonName, location.pathname);
    if (onClick) {
      onClick();
    }
  }, [buttonName, location.pathname, onClick]);
};

/**
 * Hook to track form interactions
 */
export const useFormTracking = (formName: string) => {
  const startTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    startTimeRef.current = Date.now();
  }, []);

  const trackFormStart = useCallback(() => {
    startTimeRef.current = Date.now();
  }, []);

  const trackFormSubmit = useCallback((success: boolean) => {
    const timeToComplete = Date.now() - startTimeRef.current;
    logTiming(`form_completion_${formName}`, timeToComplete, 'forms');
  }, [formName]);

  return { trackFormStart, trackFormSubmit };
};

/**
 * Hook to track scroll depth
 */
export const useScrollTracking = () => {
  const maxScrollRef = useRef<number>(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollPercent = 
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      
      if (scrollPercent > maxScrollRef.current) {
        maxScrollRef.current = scrollPercent;
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      
      // Log max scroll depth on unmount
      if (maxScrollRef.current > 0) {
        logTiming('scroll_depth', Math.round(maxScrollRef.current), location.pathname);
      }
    };
  }, [location.pathname]);
};

/**
 * Hook to track visibility/focus
 */
export const useVisibilityTracking = () => {
  const visibilityStartRef = useRef<number>(Date.now());

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        const timeVisible = Date.now() - visibilityStartRef.current;
        logTiming('active_time', timeVisible, 'engagement');
      } else {
        visibilityStartRef.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
};
